import React from 'react';
import { Clock, CheckSquare, Users, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

import InfoIcon from './InfoIcon';
import ScopeDot from './ScopeDot';
import PlanItemRow from './PlanItemRow';
import { buildCommittedProposal, getItemRoute } from './dashboardHelpers';

const TodayView = ({ planDistribution, operationalAgenda, todayStr, onQuickCall, onQuickNote, onToggleDone, navigate, readOnly = false }) => {
  const todaysItems = planDistribution?.[todayStr] || [];
  const { committed, proposal } = buildCommittedProposal(todayStr, operationalAgenda, todaysItems);
  const pending = committed.concat(proposal).filter((i) => !(i.done || i.completed_in_plan));

  // Format today's date in Spanish for mobile clarity
  const todayLabel = todayStr ? (() => {
    try {
      const d = new Date(`${todayStr}T00:00:00`);
      return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch { return ''; }
  })() : '';

  return (
    <Card className="flex flex-col md:h-full" data-testid="today-view-card">
      <CardHeader className="pb-2 flex-shrink-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>Hoy</span>
          <InfoIcon text="Tus compromisos de hoy más los 3 contactos que el sistema propone cultivar según su cadencia." testid="info-today-view" ariaLabel="Información sobre Hoy" />
          {todayLabel && (
            <span className="text-xs font-normal text-muted-foreground capitalize hidden sm:inline">
              · {todayLabel}
            </span>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {pending.length} pendiente{pending.length !== 1 ? 's' : ''}
          </span>
        </CardTitle>
        {todayLabel && (
          <p className="text-[11px] text-muted-foreground capitalize sm:hidden">
            {todayLabel}
          </p>
        )}
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col gap-3 min-h-0 md:overflow-hidden">
        <div className="flex-shrink-0 text-[10px] text-muted-foreground">
          <span data-testid="today-count-header">{committed.length} comprometidos · {proposal.length} propuestos</span>
        </div>

        {/* BLOQUE 1: COMPROMETIDO HOY */}
        <div className="md:flex-1 md:min-h-0 flex flex-col" data-testid="today-committed-block">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex-shrink-0">
            Comprometido hoy ({committed.length})
          </p>
          {committed.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Sin compromisos hoy.</p>
          ) : (
            <ScrollArea className="md:flex-1 md:min-h-0 max-h-[35vh] md:max-h-none pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {committed.map((it, i) => {
                  const Icon = it.__kind === 'activity' ? Users : it.__kind === 'deadline' || it.__kind === 'urgent_opp' ? Target : CheckSquare;
                  const overdue = it.is_overdue;
                  return (
                    <button
                      key={`cmt-${todayStr}-${it.item_type}-${it.item_id}-${i}`}
                      type="button"
                      onClick={() => navigate(getItemRoute(it))}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border hover:bg-muted/60 text-left ${overdue ? 'border-red-200 bg-red-50/50' : ''}`}
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                      <ScopeDot scope={it.scope} />
                      <span className="text-sm truncate flex-1">{it.title}</span>
                      {overdue && (
                        <span className="text-[10px] text-red-600 font-medium whitespace-nowrap">Vencida · {it.days_overdue}d</span>
                      )}
                      {it.time && !overdue && (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{it.time}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* BLOQUE 2: PROPUESTA (max 3) */}
        {proposal.length > 0 && (
          <div className="flex-shrink-0 border-t pt-2" data-testid="today-proposal-block">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              Propuesta ({proposal.length})
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              {proposal.map((it) => (
                <PlanItemRow
                  key={`prop-${it.item_type}-${it.item_id}`}
                  item={it}
                  todayStr={todayStr}
                  onQuickCall={onQuickCall}
                  onQuickNote={onQuickNote}
                  onToggleDone={onToggleDone}
                  navigate={navigate}
                  readOnly={readOnly}
                  compact
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TodayView;
